import React, { SyntheticEvent } from 'react';
import { FormControlLabel, Grid, TextField, FormControl, Select, MenuItem, InputLabel, Box, Checkbox, SelectChangeEvent } from '@mui/material';
import { Drawer, Divider, IconButton } from '@mui/material';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';

import {formatDateParam, MONTHS, AGE_GROUPS, ALL_EVENTS, ETYPES, ETYPES_DESCR, IS_ROAD, IS_TRACK, EVENT_TYPES, } from './Util';
import { PlainButton } from './Widgets';

export class Filter {
    year: number = 0;
    month: number = 0;
    event: string = '';
    gender: string = '';
    age_group: string = '';
    mainOnly: boolean = true;
    meeting: string = '';    
    fromdate: Date = new Date(0);  // UNIX epoch
    todate: Date = new Date(0);
    is_ym = true;

    constructor(year: number = 0, month: number = 0, event: string = '', gender: string = '', age_group: string = '', mainOnly: boolean = true, meeting: string = '') {
        this.year = year;
        this.month = month;
        this.event = event;
        this.gender = gender;
        this.age_group = age_group;
        this.mainOnly = mainOnly;
        this.meeting = meeting;
    }

    /*** ONLY STATIC METHODS AS WE NEED TO DEEP COPY THE OBJECT, AND IT BECOMES INSTANCE OF SOMETHINE ELSE... WTF ***/
    static DateRangeFilter(fromdate: Date, todate: Date, event: string = '', gender: string = '', age_group: string = '', mainOnly: boolean = true, meeting: string = '') {
        var filter: Filter = new Filter(0, 0, event, gender, age_group, mainOnly, meeting);
        filter.fromdate = fromdate;
        filter.todate = todate;
        filter.is_ym = false;
        return filter;
    }

    static get_params(filter: Filter) {
        var str;
        if (filter.is_ym) {
            str = "year=" + filter.year + "&month=" + filter.month;
        } else {
            str = "fromdate=" + formatDateParam(filter.fromdate) + "&todate=" + formatDateParam(filter.todate);
        }

        str += "&event=" + filter.event + "&gender=" + filter.gender + "&age_group=" + filter.age_group + "&meeting=" + filter.meeting;
        return str; 
    }
}

export class FilterPrefs {
    year: boolean = false;
    month: boolean = false;
    event: boolean = false;
    gender: boolean = false;
    age_group: boolean = false;
    mainOnly: boolean = false;
    meeting: boolean = false;
    all_event: boolean = false;
    all_gender: boolean = false;
    all_age_group: boolean = false;
}

type EventButtonProps = {
    type: number;
    is_selected: boolean;
    dkey: string;
    text: string;
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
}

function EventButton(props: EventButtonProps) {   
    var bg = props.type === IS_ROAD ? 'var(--table-header-color)' :
        (props.type === IS_TRACK ? 'var(--table-alt-header-color)' : 'var(--secondary-color)');
    var className = props.is_selected ? 'event-button event-button-selected' : 'event-button';
    const isAll = props.dkey === 'ALL';
    return (
    <div
        onClick={props.onClick}
        data-key={props.dkey}
        className={className}
        style={{
            backgroundColor: isAll
                ? 'var(--paper-color)'
                : bg,
            color: isAll
                ? 'var(--plain-color)'
                : 'var(--light-text-color)'
        }}
        key={props.dkey}
    >
        {props.text}
    </div>
    );
}

interface EventKeysProps { 
    onSelectEvent: (e: React.MouseEvent<HTMLDivElement>) => void;
    selection: string;
    has_all: boolean;
}

interface EventKeysState {
}

class EventKeys extends React.Component<EventKeysProps, EventKeysState> {
    render() {
        var events_road = ALL_EVENTS.map((ev, ind) => { 
            return EVENT_TYPES[ind] === IS_ROAD ? 
                <EventButton onClick={this.props.onSelectEvent} key={ev} dkey={ev} text={ev} type={IS_ROAD} is_selected={this.props.selection === ev} />
                : null;
        })

        var events_track = ALL_EVENTS.map((ev, ind) => {
            return EVENT_TYPES[ind] === IS_TRACK ? 
                <EventButton onClick={this.props.onSelectEvent} key={ev} dkey={ev} text={ev} type={IS_TRACK} is_selected={this.props.selection === ev} />
                : null;
        })

        var etypes = ETYPES.map((ev, ind) => {
            // skips road and track, for which we detail all events
            return (ev !== 'R' && ev !== 'T') ? <EventButton onClick={this.props.onSelectEvent} key={ev} dkey={ev} text={ETYPES_DESCR[ind]} type={0} is_selected={this.props.selection === ev} />
            : null;
        })

        return (
            <div className="filter-event-groups">
                <div className="filter-section-title">Events</div>

                {this.props.has_all && (
                <div className="event-group" style={{ marginBottom: '4px' }}>
                    <EventButton onClick={this.props.onSelectEvent} dkey="ALL" text="All events" type={0} is_selected={this.props.selection === ''} />
                </div>
                )}

                <div className="event-group-label">Road</div>
                <div className="event-group">{events_road}</div>

                <div className="event-group-label">Track</div>
                <div className="event-group">{events_track}</div>

                <div className="event-group-label">Other</div>
                <div className="event-group">{etypes}</div>
            </div>
        );    
    }
}


interface FilterPanelProps {
    onChange: (filter: Filter, requery: boolean) => void;
    onEsc: () => void;
    prefs: FilterPrefs;
    start_filter: Filter;
}

interface FilterPanelState {
   filter: Filter;
}

class FilterPanel extends React.Component<FilterPanelProps, FilterPanelState> {
    constructor(props: FilterPanelProps) {
        super(props);

        this.state = {
            filter: this.props.start_filter,
        };

        this.handleAgeGroup = this.handleAgeGroup.bind(this);
        this.handleGender = this.handleGender.bind(this);
        this.handleMain = this.handleMain.bind(this);
        this.handleYear = this.handleYear.bind(this);
        this.handleMonth = this.handleMonth.bind(this);
        this.onSelectEvent = this.onSelectEvent.bind(this);
    }

    componentDidMount() {
        this.onChange(false);
    }

    onChange(requery: boolean) {
        this.props.onChange(this.state.filter, requery); 
    }

    handleYear(e: SelectChangeEvent) {
        var newfilter = this.state.filter;
        newfilter.year = parseInt(e.target.value);
        this.setState({filter: newfilter}, () => { this.onChange(true);  });
    }

    handleMonth(e: SelectChangeEvent) {
        var newfilter = this.state.filter;
        newfilter.month = parseInt(e.target.value);
        this.setState({filter: newfilter}, () => { this.onChange(true);  });
    }
    
    handleAgeGroup(e: SelectChangeEvent) {
        var newfilter = this.state.filter;
        newfilter.age_group = e.target.value === 'ALL' ? '' : e.target.value;
        this.setState({filter: newfilter}, () => { this.onChange(true);  });
    }
    
    handleGender(gender: string) {
        const newfilter = { ...this.state.filter };

        if (this.props.prefs.all_gender && newfilter.gender === gender) {
            newfilter.gender = '';
        } else {
            newfilter.gender = gender;
        }

        this.setState({ filter: newfilter }, () => {
            this.onChange(true);
        });
    }

    handleMain(e: SyntheticEvent<Element, Event>, checked: boolean) {
        var newfilter = this.state.filter;
        newfilter.mainOnly = !this.state.filter.mainOnly; // we invert the value (the checkbox is "controlled")
        this.setState({filter: newfilter}, () => { this.onChange(false);  });
    }

    onSelectEvent(e: React.MouseEvent<HTMLDivElement>) {
        const key = e.currentTarget.dataset.key;
        if (!key) return;
        const ev_key: string = key;
        var newfilter = this.state.filter;
        newfilter.event = ev_key === 'ALL' ? '' : ev_key;
        this.setState({filter: newfilter}, () => { this.onChange(true);  });
    }

    render() {
        var this_year = new Date().getFullYear();
        var year_array = Array.from({length: this_year-2000+1}, (_, i) => this_year - i)
        var year_filter = <FormControl fullWidth margin='dense' className="filter-form-control">
            <InputLabel className="filter-label">Year</InputLabel>
            <Select variant="outlined" value={this.state.filter.year.toString()} label="Year" onChange={this.handleYear} sx={{ fontSize: '1.35rem' }} >
                <MenuItem key={0} value={0}>{'ALL'}</MenuItem>
                {year_array.map((y, ind) => { return <MenuItem key={y} value={y}>{y}</MenuItem> })}
            </Select>
        </FormControl>

        var month_filter = <FormControl fullWidth margin='dense' className="filter-form-control">
            <InputLabel className="filter-label">Month</InputLabel>
            <Select variant="outlined" value={this.state.filter.month.toString()} label="Month" onChange={this.handleMonth} sx={{ fontSize: '1.35rem' }} >
                <MenuItem key={0} value={0}>{'ALL'}</MenuItem>
                {MONTHS.map((m, ind) => { return <MenuItem key={ind+1} value={ind+1}>{m}</MenuItem> })}
            </Select>
            </FormControl>

        var event_keys = <EventKeys onSelectEvent={this.onSelectEvent} selection={this.state.filter.event} has_all={this.props.prefs.all_event} />

        var main_filter = (
            <FormControlLabel
                className="filter-checkbox"
                control={<Checkbox />}
                label="Main events only"
                onChange={this.handleMain}
                checked={this.state.filter.mainOnly}
            />
        );

        /*
        var gender_filter = <FormControl fullWidth margin='dense' className="filter-form-control">
            <InputLabel className="filter-label">Gender</InputLabel>
            <Select variant="outlined" value={this.state.filter.gender} label="Gender" onChange={this.handleGender} sx={{ fontSize: '1.35rem' }}>
                {this.props.prefs.all_gender && <MenuItem key='ALL' value=''>ALL</MenuItem>}
                <MenuItem key='W' value='W'>Women</MenuItem>
                <MenuItem key='M' value='M'>Men</MenuItem>
            </Select>
        </FormControl>
        */

        var gender_filter = (
            <div className="filter-gender-toggle">
                <button
                    type="button"
                    className={
                        this.state.filter.gender === 'M'
                            ? 'filter-chip filter-chip-selected'
                            : 'filter-chip'
                    }
                    onClick={() => this.handleGender('M')}
                >
                    Men
                </button>

                <button
                    type="button"
                    className={
                        this.state.filter.gender === 'W'
                            ? 'filter-chip filter-chip-selected'
                            : 'filter-chip'
                    }
                    onClick={() => this.handleGender('W')}
                >
                    Women
                </button>
            </div>
        );

        var ag_filter = <FormControl fullWidth margin='dense' className="filter-form-control">
            <InputLabel className="filter-label">Age Group</InputLabel>
            <Select variant="outlined" value={this.state.filter.age_group} label="Age Group" onChange={this.handleAgeGroup} sx={{ fontSize: '1.35rem' }}>
                {AGE_GROUPS.map((ag, ind) => { return <MenuItem key={ag} value={ag}>{ag}</MenuItem> })}
            </Select>
        </FormControl>
    
        var filter = <Box className="filter-panel" onClick={this.props.onEsc}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} className="filter-section">{this.props.prefs.gender && gender_filter}</Grid>
                            <Grid item xs={6} className="filter-section">{this.props.prefs.age_group && ag_filter}</Grid>
                            <Grid item xs={6} className="filter-section">{this.props.prefs.year && year_filter}</Grid>
                            <Grid item xs={6} className="filter-section">{this.props.prefs.month && month_filter}</Grid>
                            <Grid item xs={12} className="filter-section">{this.props.prefs.event && event_keys}</Grid>                    
                            <Grid item xs={12} className="filter-section">{this.props.prefs.mainOnly && main_filter}</Grid>
                        </Grid>
                    </Box>;

            return filter;
    }
}

// prefs: year, month, event, all_event, main, gender, all_gender, ag, venue
interface FilterDrawerProps {
    onChange: (filter: Filter, requery: boolean) => void;
    prefs: FilterPrefs;
    start_filter: Filter;
    display_filter: boolean;
}

interface FilterDrawerState {
    is_open: boolean;
    filter_label: string;
    filter: Filter;
}

export class FilterDrawer extends React.Component<FilterDrawerProps, FilterDrawerState> {
    constructor(props: FilterDrawerProps) {
        super(props);
        this.state = { is_open: false,
            filter: this.props.start_filter,
            filter_label: FilterDrawer.filterString(this.props.prefs, this.props.start_filter),
            }
        this.onChange = this.onChange.bind(this);
        this.onEsc = this.onEsc.bind(this);
        this.toggleDrawer = this.toggleDrawer.bind(this);
        this.handleMeeting = this.handleMeeting.bind(this);
    }

    static add_label(curr: string, add: string) {
        if (add === '')
            return curr;
        if (curr === '')
            return add;
        return curr + " | " + add;
    }
    
    static filterString(prefs: FilterPrefs, filter: Filter) {
        var date: string = '';
        if (prefs.year && prefs.month && filter.year > 0) {
            date = filter.month > 0 ? MONTHS[filter.month-1] + "-" + filter.year : filter.year.toFixed(0);
        } else if (prefs.year && filter.year > 0) {
            date = filter.year.toFixed(0);
        }

        var event = '';
        if (prefs.event && filter.event !== '') {
            var ind = ETYPES.indexOf(filter.event);
            event = (ind >= 0) ? ETYPES_DESCR[ind]: filter.event;
        }

        var label = date;
        label = FilterDrawer.add_label(label, event);
        label = FilterDrawer.add_label(label, prefs.gender ? (filter.gender !== '' ? (filter.gender === 'W' ? 'Women' : 'Men') : '') : '');
        label = FilterDrawer.add_label(label, prefs.age_group ? (filter.age_group !== '' ? filter.age_group : ''): '');
    
        return label;
    }    

    toggleDrawer(event: React.MouseEvent<HTMLButtonElement>) {
        this.setState({ is_open: !this.state.is_open });
    };

    onChange(filter: Filter, requery: boolean) {
        var newfilter = filter;
        newfilter.meeting = this.state.filter.meeting;
        this.setState({filter: newfilter,
                            filter_label: FilterDrawer.filterString(this.props.prefs, newfilter)}, () => { this.props.onChange(newfilter, requery) });
    }

    onEsc() {
        this.setState({ is_open: false });
    }

    handleMeeting(event: React.ChangeEvent<HTMLInputElement>) {
        var newfilter = this.state.filter;
        newfilter.meeting = event.target.value;
        this.setState({filter: newfilter}, () => { this.props.onChange(newfilter, true) });
    }

    render() {
        var searchbox = <></>;
        if (this.props.prefs.meeting) {
            var searchtext = <TextField
                                    placeholder="Venue"
                                    variant="standard"
                                    value={this.state.filter.meeting}
                                    onChange={this.handleMeeting}
                                    size='small'
                                    sx={{
                                        flex: 1,
                                        input: {
                                        fontSize: '1.5rem',
                                        color: 'var(--light-text-color)',
                                        padding: '6px 4px',
                                        }
                                    }}
                                    InputProps={{
                                        disableUnderline: true
                                    }}
                                />

            searchbox = <div className="searchbox">
                            <SearchIcon className="search-icon" />
                            {searchtext}
                        </div>                
        }

        var drawer_button = (this.props.display_filter && this.state.filter_label !== '') ?
                                <PlainButton
                                    action={this.toggleDrawer}
                                    style={{color: 'var(--light-text-color)', backgroundColor: 'var(--secondary-color)', border: '0px'}} 
                                    title={this.state.filter_label} 
                                /> :
                                <IconButton
                                    className="filter-button"
                                    onClick={this.toggleDrawer}
                                    style={{ color: 'var(--light-text-color)' }}
                                >
                                    <FilterAltIcon fontSize="large" />
                                </IconButton>

        return <Box style={{left: 'auto', right: '0', maxWidth: '60%'}}>
                    <div className="filter-toolbar">
                        {searchbox}
                        <div style={{flexGrow: '0', flexShrink: '1'}} >
                        {drawer_button}
                    </div>
                </div>
                <Drawer
                    variant="persistent"
                    anchor="right"
                    open={this.state.is_open}
                >
                    <div className="filter-drawer-header">
                    <IconButton onClick={this.toggleDrawer}>
                        <ChevronRightIcon fontSize="large" />
                    </IconButton>
                    </div>
                    <Divider />
                    <Box className="filter-drawer" role="presentation" sx={{ overflowY: 'auto', maxHeight: '100vh' }}>
                        <FilterPanel onChange={this.onChange} onEsc={this.onEsc} prefs={this.props.prefs} start_filter={this.props.start_filter} />
                    </Box>
                </Drawer>
            </Box>
    }
}
